import React, { useState } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography} from '@mui/material';
import { Grid } from '@mui/material';
import { Coupon } from 'common/contracts';
import { FormikErrors } from 'formik';
import { FormikTouched } from 'formik';
import { capitalizeFirstLetter } from 'utils/formattingUtil';

import AcCard from 'components/AcCard/AcCard';
import AcSelect from 'components/AcSelect/AcSelect';

interface GeneralSettingsProps {
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  errors: FormikErrors<Coupon>;
  touched: FormikTouched<Coupon>;
  setFieldValue: (field: string, value: any) => void;
  getOffersForSelect: (supportedOffersByType?: string[]) => any[];
  supportedOffersBySku: string[];
}

const promoCodeTypes = ['promotions', 'bundles', 'popups'];

export default function CouponsOffersSettings({
  handleBlur,
  errors,
  touched,
  setFieldValue,
  getOffersForSelect,
  supportedOffersBySku
}: GeneralSettingsProps) {
  const [selectedOfferTypes, setSelectedOfferTypes] =
    useState<string[]>(promoCodeTypes);

  const [offerSelectionMode, setOfferSelectionMode] = useState<
    'select' | 'all'
  >(supportedOffersBySku && supportedOffersBySku.length > 0 ? 'select' : 'all');

  const handleOfferSelectionModeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const mode = event.target.value as 'select' | 'all';
    setOfferSelectionMode(mode);
    if (mode === 'all') {
      setSelectedOfferTypes([]);
      setFieldValue('supportedOffersBySku', []);
    }
  };

  return (
    <AcCard
      stackContainer={false}
      title="Offers Settings"
      customStyles={{
        border: '1px solid #cacbd4',
        borderRadius: '4px',
        padding: '1em',
        marginTop: '1em'
      }}
    >
      <RadioGroup
        row
        name="offerSelectionMode"
        value={offerSelectionMode}
        onChange={handleOfferSelectionModeChange}
        sx={{ mb: 2 }}
      >
        <FormControlLabel
          value="all"
          control={<Radio />}
          label={
            <Stack direction="row" alignItems="center">
              <Typography>All Offers</Typography>
              <Tooltip title="Selecting this will make the coupon available for all offers also created in the future">
                <InfoOutlinedIcon
                  sx={{ ml: 0.5, fontSize: '1rem', color: 'text.secondary' }}
                />
              </Tooltip>
            </Stack>
          }
        />
        <FormControlLabel
          value="select"
          control={<Radio />}
          label="Select Offers"
        />
      </RadioGroup>
      {offerSelectionMode === 'select' && (
        <Grid container columnSpacing={1.5}>
          <Grid xs={4} item>
            <AcSelect
              header="Eligible offer types"
              isSelectAllEnabled
              name="supportedOffersByType"
              value={selectedOfferTypes}
              onChange={(e: SelectChangeEvent<unknown>) => {
                const newSelectedTypes = e.target.value as string[];
                setSelectedOfferTypes(newSelectedTypes);
                setFieldValue(
                  'supportedOffersBySku',
                  getOffersForSelect(newSelectedTypes).map((offer) => offer.key)
                );
              }}
              onBlur={handleBlur}
              isCheckBox
              items={promoCodeTypes.map((type) => ({
                content: capitalizeFirstLetter(type),
                key: type,
                value: type
              }))}
              setSelectedItems={(selected: any) => {
                setSelectedOfferTypes(selected);
                setFieldValue(
                  'supportedOffersBySku',
                  getOffersForSelect(selected as any[]).map(
                    (offers) => offers.key
                  )
                );
              }}
              renderValue={(selected: any) => {
                if (selected.length === 3) return 'All offer types';
                return selected
                  ?.map((item: string) => {
                    return item?.charAt(0)?.toUpperCase() + item?.slice(1);
                  })
                  .join(', ');
              }}
            />
          </Grid>
          <Grid xs={4} item>
            <AcSelect
              isSearchEnabled={true}
              header="Offers"
              isSelectAllEnabled
              name="supportedOffersBySku"
              value={supportedOffersBySku}
              onChange={(e: SelectChangeEvent<unknown>) => {
                setFieldValue(
                  'supportedOffersBySku',
                  e.target.value as string[]
                );
              }}
              onBlur={handleBlur}
              isCheckBox
              items={getOffersForSelect(selectedOfferTypes)}
              setSelectedItems={(selected: any) => {
                const selectedOfferTypesKeys = getOffersForSelect(
                  selectedOfferTypes
                )
                  .map((item: any) =>
                    selected.includes(item.value) ? item.key : null
                  )
                  .filter(Boolean);

                setFieldValue('supportedOffersBySku', selectedOfferTypesKeys);
              }}
              renderValue={(selected: any) => {
                if (selected.length === 0) return '';
                if (
                  selected.length ===
                  getOffersForSelect(selectedOfferTypes).length
                )
                  return `All ${selectedOfferTypes.join(',')}`;
                return selected.length > 2
                  ? `${selected.length} Offers selected`
                  : selected
                      ?.map((item: string) => {
                        return item?.charAt(0)?.toUpperCase() + item?.slice(1);
                      })
                      .join(', ');
              }}
              error={
                touched.supportedOffersBySku &&
                Boolean(errors.supportedOffersBySku)
              }
              helperText={
                touched.supportedOffersBySku
                  ? errors.supportedOffersBySku?.toString()
                  : ''
              }
            />
          </Grid>
        </Grid>
      )}
    </AcCard>
  );
}
